import React, { Suspense, useState, useEffect, useCallback } from "react";
import "./App.css";
import Login from "@features/auth/components/LoginV2";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import useToken from "@shared/hooks/useToken";
import { withTranslation } from "react-i18next";
import { BrandingContext } from "@shared/branding/BrandingContext";
import routes from "./config/routes";
import ProtectedRoute from "@shared/auth/ProtectedRoute";
import { AuthProvider } from "@shared/auth/AuthProvider";
import { getBrandingSettings } from "@shared/branding/brandingConfig";
import { applyPwaLinks, setFavicon } from "@shared/branding/pwa";
import {
  applyTenantBranding,
  fetchTenantBranding,
  getCachedTenantBranding,
  getTenantId,
  resetTenantBranding,
  storeTenantBranding,
} from "@shared/branding/tenantBranding";
import Alerts from "@shared/components/Alerts";
import { SESSION_EXPIRED_FLAG } from "@shared/api/http";
import { startLiveUpdates } from "@shared/mobile/liveUpdate";
import { syncNativePush } from "@shared/push/nativePush";
import NativePushLinks from "@shared/push/NativePushLinks";

// How long a first visit from a tenant link waits for its branding before
// falling back to sophyapp.
const TENANT_WAIT_MS = 3000;

function App() {
  const hostname = window.location.hostname;
  const brand = getBrandingSettings(hostname);
  // A domain brand (training.medfit.ch, …) always wins. Elsewhere a tenant
  // brand from a ?m= link or the last login applies (tenantBranding.js); the
  // cached one paints the first frame and is refreshed below.
  const isDomainBrand = brand.name !== "sophyapp";
  const [tenant, setTenant] = useState(() =>
    isDomainBrand ? null : getCachedTenantBranding()
  );
  // First visit from a tenant link: nothing cached yet, so show a neutral
  // spinner instead of flashing the sophyapp login until the branding arrives.
  const [waitingForTenant, setWaitingForTenant] = useState(
    () => !isDomainBrand && Boolean(getTenantId()) && !tenant
  );
  // The backend style key (registration, password mails): tenants run on
  // the sophyapp style, their look comes from the tenant payload.
  const applicationName = brand.name;
  const applicationImagePath = tenant?.logo_url || brand.imagePath;
  const { token, setToken } = useToken();
  const [sessionExpiredAlertOpen, setSessionExpiredAlertOpen] = useState(false);

  // Store app only: having rendered proves this web bundle works (see liveUpdate.js).
  useEffect(() => {
    startLiveUpdates();
  }, []);

  // Store app only: keep this device's push token current (see nativePush.js).
  useEffect(() => {
    if (token) syncNativePush();
  }, [token]);

  useEffect(() => {
    if (localStorage.getItem(SESSION_EXPIRED_FLAG) === "1") {
      setSessionExpiredAlertOpen(true);
      localStorage.removeItem(SESSION_EXPIRED_FLAG);
    }
  }, []);

  // outsource and manage in index.html
  useEffect(() => {
    // const hostname = window.location.hostname;
    // if (hostname.endsWith("medfit.ch")) {
    //   import("./styles/medfit.css").then(() => {});
    //   setApplicationName("medfit");
    //   setApplicationImagePath("/images/ci/medfit/medfit1.jpg");
    //   var link = document.querySelector("link[rel~='icon']");
    //   if (!link) {
    //     link = document.createElement("link");
    //     link.rel = "icon";
    //     document.head.appendChild(link);
    //   }
    //   link.href = "/images/ci/medfit/faviconV2.ico";
    //   document.title = "Fit für Morgen!";
    // } else if (hostname.endsWith("medaix-exercise.de")) {
    //   import("./styles/medaix.css").then(() => {});
    //   setApplicationName("medaix");
    //   setApplicationImagePath("/images/ci/medaix/medaix_logo.png");
    //   document.title = "MedAix Exercise";
    // } else {
    //   import("./styles/variables.css").then(() => {});
    // }
    const cssPath = brand.css;
    import(`${cssPath}`).then(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Colours, title, favicon and home-screen tags: the tenant's on top of the
  // sophyapp theme, or the brand's own.
  useEffect(() => {
    resetTenantBranding();
    if (tenant) {
      applyTenantBranding(tenant);
      return;
    }
    document.title = brand.title;
    applyPwaLinks(brand);
    setFavicon(brand.icon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  useEffect(() => {
    const tenantId = getTenantId();
    if (isDomainBrand || !tenantId) return;
    const stopWaiting = () => setWaitingForTenant(false);
    const timeout = setTimeout(stopWaiting, TENANT_WAIT_MS);
    fetchTenantBranding(tenantId)
      .then(setTenant)
      // Offline or server error: keep the cached brand.
      .catch(() => {})
      .finally(() => {
        clearTimeout(timeout);
        stopWaiting();
      });
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a login the account's brand wins. Returns the tenant that applies
  // from now on. Backends without the `branding` key leave it unchanged.
  const applyAccountBranding = useCallback(
    (loginResponse) => {
      if (isDomainBrand) return null;
      if (!loginResponse || !("branding" in loginResponse)) return tenant;
      const accountTenant =
        loginResponse.branding?.id != null ? loginResponse.branding : null;
      storeTenantBranding(accountTenant);
      setTenant(accountTenant);
      return accountTenant;
    },
    [isDomainBrand, tenant]
  );

  const branding = {
    applicationName,
    applicationImagePath,
    brandName: tenant?.name || brand.name,
    isBrandingCustomer: isDomainBrand || Boolean(tenant),
    tenantId: tenant ? tenant.id : null,
    tenant,
    therapistAppUrl: brand.therapistUrl,
    applyAccountBranding,
  };

  if (waitingForTenant) {
    return (
      <div className="tenant-loading" role="status" aria-label="Laden">
        <div className="tenant-loading-spinner" />
      </div>
    );
  }

  return (
    <BrandingContext.Provider value={branding}>
      <Suspense fallback="loading">
        <AuthProvider>
          <div className="main_background">
            <BrowserRouter>
              <NativePushLinks />
              <Routes>
                {routes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={
                      route.isProtected ? (
                        token ? (
                          <ProtectedRoute>{route.element}</ProtectedRoute>
                        ) : (
                          <Login
                            setToken={setToken}
                            application={applicationName}
                            imagePath={applicationImagePath}
                          />
                        )
                      ) : (
                        route.element
                      )
                    }
                  />
                ))}
              </Routes>
            </BrowserRouter>
          </div>
          <Alerts
            open={sessionExpiredAlertOpen}
            message="Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an."
            severity="warning"
            onClose={() => setSessionExpiredAlertOpen(false)}
          />
        </AuthProvider>
      </Suspense>
    </BrandingContext.Provider>
  );
}

export default withTranslation()(App);
